// BallotStep.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { DndContext, closestCenter } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { BallotRow } from './BallotRow'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Info } from 'lucide-react'
import { useHall } from '@/contexts/HallProvider'
import { useAxios } from '@/contexts/AxiosProvider'
import { SpeakingRole } from '@db8/shared'
import { toast } from 'react-toastify'

function deepEqual(a: any, b: any) {
  return JSON.stringify(a) === JSON.stringify(b)
}

export function useAutoSaveToast(data: any) {
  const hasMounted = useRef(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastData = useRef(data)

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      lastData.current = data
      return
    }

    if (deepEqual(data, lastData.current)) return

    lastData.current = data

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      toast.info('Autosaved draft...', {
        autoClose: 500,
        position: 'bottom-center',
      })
      timeoutRef.current = null
    }, 3000)
  }, [data])
}

export const BallotStep: React.FC<{
  setIsAIProcessing: (val: boolean) => void
  forwardStep: () => void
}> = ({ setIsAIProcessing, forwardStep }) => {
  const { currentUser, teamMembers, getUser } = useHallUsers()
  const [rows, setRows] = useState<Array<any>>([])
  const [editingFeedbackRowId, setEditingFeedbackRowId] = useState<string | null>(null)
  const { hall, transcriptions, handleBallot, ballots, hallMetadata } = useHall()
  const [feedbackDraft, setFeedbackDraft] = useState('')
  const [decision, setDecision] = useState<'Affirmative' | 'Negative' | null>(null)
  const [isLowPointWin, setIsLowPointWin] = useState(false)
  const [reasons, setReasons] = useState('')
  const [comments, setComments] = useState('')
  const [ballotId, setBallotId] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const axios = useAxios()

  // Build unique team list (ids coerced to string)
  const team = useMemo(() => {
    const map = new Map<string, any>()
    teamMembers
      .map((uid) => getUser(uid))
      .filter(Boolean)
      .forEach((u: any) => {
        const id = String(u.id)
        if (!map.has(id)) map.set(id, u)
      })
    return Array.from(map.values())
  }, [teamMembers, getUser])

  const validateBallot = () => {
    const allScored = rows.every((row) => typeof row.points === 'number' && row.points > 0)
    return Boolean(decision && allScored && hallMetadata?.pollingStarted)
  }

  const DRAFT_KEY = useMemo(() => {
    if (!hall?.id || !currentUser?.id) return ''
    return `ballot-draft-${hall.id}-${currentUser.id}`
  }, [hall?.id, currentUser?.id])

  const setDefaults = () => {
    const byId = new Map<string, any>()
    for (const user of team) {
      const id = String(user.id)
      if (!byId.has(id)) byId.set(id, { id, user, points: 0, feedback: '' })
    }
    setRows(Array.from(byId.values()))
    setDecision(null)
    setComments('')
    setReasons('')
    setFeedbackDraft('')
    setIsLowPointWin(false)
  }

  const usersBallot = useMemo(() => {
    if (!currentUser || !ballots) return null
    return ballots.find((ballot) => ballot?.hallUserId === currentUser.id)
  }, [ballots, currentUser])

  // Load existing ballot from DB / localStorage, if can't init new one
  useEffect(() => {
    const loadBallot = async () => {
      try {
        if (usersBallot) {
          setDecision(
            usersBallot.decision === 'AFFIRMATIVE'
              ? SpeakingRole.Affirmative
              : SpeakingRole.Negative,
          )
          setIsLowPointWin(usersBallot.lowPointWin)
          setReasons(usersBallot.reason)
          setComments(usersBallot.comments)
          setBallotId(String(usersBallot.id))

          const sortedFeedbacks = [...usersBallot.feedbacks].sort((a, b) => a.rank - b.rank)

          const loadedRows = sortedFeedbacks
            .map((fb) => {
              const uid = String(fb.hallUserId)
              const userMatch = team.find((u) => String(u.id) === uid)
              if (!userMatch) return null
              return {
                id: uid,
                user: userMatch,
                points: fb.points,
                feedback: fb.content,
              }
            })
            .filter(Boolean) as any[]

          // de-dupe just in case
          const byId = new Map<string, any>()
          for (const r of loadedRows) if (!byId.has(r.id)) byId.set(r.id, r)
          setRows(Array.from(byId.values()))
        } else {
          const draft = DRAFT_KEY ? localStorage.getItem(DRAFT_KEY) : null
          if (draft) {
            const parsed = JSON.parse(draft)
            setDecision(parsed.decision || null)
            setIsLowPointWin(parsed.lowPointWin || false)
            setReasons(parsed.reasons || '')
            setComments(parsed.comments || '')

            const teamMap = new Map(team.map((u) => [String(u.id), u]))
            const loaded: any[] = []

            for (const row of parsed.rows || []) {
              const id = String(row.id)
              if (teamMap.has(id)) {
                loaded.push({
                  id,
                  user: teamMap.get(id),
                  points: row.points ?? 0,
                  feedback: row.feedback ?? '',
                })
              }
            }

            // add missing team members
            const seen = new Set(loaded.map((r) => r.id))
            for (const u of team) {
              const id = String(u.id)
              if (!seen.has(id)) loaded.push({ id, user: u, points: 0, feedback: '' })
            }

            // de-dupe
            const byId = new Map<string, any>()
            for (const r of loaded) if (!byId.has(r.id)) byId.set(r.id, r)
            setRows(Array.from(byId.values()))
          } else {
            setDefaults()
          }
        }
      } catch (err) {
        // no-op
      }
    }

    if (hall?.id || currentUser?.id || team.length > 0) loadBallot()
  }, [hall, currentUser, team, ballots, usersBallot, DRAFT_KEY])

  // Sync draft to localStorage
  useEffect(() => {
    if (!hall?.id || !currentUser?.id || rows.length === 0) return
    const draftData = { decision, lowPointWin: isLowPointWin, reasons, comments, rows }
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draftData))
  }, [decision, isLowPointWin, reasons, comments, rows, hall?.id, currentUser?.id, DRAFT_KEY])

  // Stable list of IDs used by SortableContext and keys
  const itemIds = useMemo(() => {
    const ids = rows.map((r) => String(r.id))
    return Array.from(new Set(ids)) // ensure uniqueness
  }, [rows])

  const handleDragEnd = ({ active, over }: any) => {
    if (!over || active.id === over.id) return
    setRows((items) => {
      const from = items.findIndex((i) => String(i.id) === String(active.id))
      const to = items.findIndex((i) => String(i.id) === String(over.id))
      if (from === -1 || to === -1) return items
      return arrayMove(items, from, to)
    })
  }

  const handlePointsChange = (id: string, points: number) => {
    setRows((prev) => prev.map((row) => (String(row.id) === String(id) ? { ...row, points } : row)))
  }

  const handleEditFeedback = (id: string, currentFeedback: string) => {
    setEditingFeedbackRowId(String(id))
    setFeedbackDraft(currentFeedback)
  }

  const handleFeedbackSubmit = () => {
    setRows((prev) =>
      prev.map((row) =>
        String(row.id) === String(editingFeedbackRowId) ? { ...row, feedback: feedbackDraft } : row,
      ),
    )
    setEditingFeedbackRowId(null)
  }

  const handleDelete = (id: string) => {
    setRows((prev) => prev.filter((row) => String(row.id) !== String(id)))
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      const speakerResults = rows.map((row, i) => ({
        hallUserId: row.user.id,
        rank: i + 1,
        points: row.points,
        content: row.feedback || '',
      }))

      const result = {
        hallId: hall.id,
        decision: decision?.toUpperCase(),
        reason: reasons || 'N/A',
        comments: comments || 'N/A',
        lowPointWin: isLowPointWin,
        submitted: true,
        feedbacks: speakerResults,
        judgeId: currentUser.id,
      }

      if (usersBallot) {
        const res = await axios.put(`/ballots/${ballotId}`, result)
        const updatedBallots = ballots.map((b) => (b.id === ballotId ? res.data : b))
        handleBallot(updatedBallots)
      } else {
        const res = await axios.post(`/ballots`, result)
        handleBallot([...ballots, res.data])
      }

      localStorage.removeItem(DRAFT_KEY)
      forwardStep()
      toast.success(`Ballot Uploaded Successfully`, {
        position: 'bottom-center',
        autoClose: 3000,
        theme: 'light',
      })
    } catch (error) {
      toast.error('Failed to submit ballot')
    } finally {
      setIsSubmitting(false)
    }
  }

  function safeParseJSON(raw: string) {
    try {
      const match = raw.match(/\{[\s\S]*\}/)
      if (!match) return null
      return JSON.parse(match[0])
    } catch {
      return null
    }
  }

  const completeWithAI = async () => {
    try {
      setIsAIProcessing(true)

      const { data } = await axios.post(`/ballots/complete`, {
        transcriptions,
        resolution: hall.topic,
      })

      let result = data

      if (!result.feedbacks) {
        if (result.raw) {
          const parsed = safeParseJSON(result.raw)
          if (parsed && parsed.feedbacks) {
            result = parsed
          } else {
            toast.error('AI returned unstructured data.')
            setIsAIProcessing(false)
            return
          }
        } else {
          toast.error('AI response malformed')
          setIsAIProcessing(false)
          return
        }
      }

      const sortedFeedbacks = [...result.feedbacks].sort((a, b) => a.rank - b.rank)
      const validUserIds = new Set(team.map((u) => String(u.id)))

      const updatedRows = sortedFeedbacks
        .filter((fb) => validUserIds.has(String(fb.hallUserId)))
        .map((fb) => {
          const user = getUser(fb.hallUserId)
          return {
            id: String(fb.hallUserId),
            user,
            points: fb.points,
            feedback: fb.comment,
          }
        })

      if (updatedRows.length === 0) {
        toast.error('AI feedback did not match any valid users.')
        return
      }

      setRows(updatedRows)

      setDecision(
        result.decision?.toUpperCase() === 'AFFIRMATIVE'
          ? SpeakingRole.Affirmative
          : SpeakingRole.Negative,
      )
      setReasons(result.reason || '')
      setComments(result.globalComment || '')
    } catch (err) {
      toast.error('AI failed to complete ballot')
    } finally {
      setIsAIProcessing(false)
    }
  }

  const disableAI = useMemo(
    () => rows.length === 0 || hallMetadata.activeTurn <= 0,
    [hallMetadata.activeTurn, rows.length],
  )

  return (
    <div className="space-y-6 h-full">
      <DialogTitle className="text-lg font-bold">Ballot</DialogTitle>

      {editingFeedbackRowId ? (
        <div className="flex flex-col space-y-4 min-w-[40vw]">
          <Textarea
            className="w-full p-2 border-gray-300 dark:text-white rounded"
            value={feedbackDraft}
            onChange={(e) => setFeedbackDraft(e.target.value)}
            placeholder="Edit feedback..."
            rows={10}
          />
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={handleFeedbackSubmit}>
              Save Feedback
            </Button>
            <Button variant="outline" onClick={() => setEditingFeedbackRowId(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-2 max-h-[min(360px,50dvh)] overflow-y-auto noScrollbar min-w-0">
                {rows.length > 0 ? (
                  rows.map((row, idx) => (
                    <BallotRow
                      key={String(row.id)}
                      id={String(row.id)}
                      rank={idx + 1}
                      totalRows={rows.length}
                      user={row.user}
                      points={row.points}
                      feedback={row.feedback}
                      onPointsChange={handlePointsChange}
                      onFeedbackChange={handleEditFeedback}
                      onDelete={handleDelete}
                    />
                  ))
                ) : (
                  <p className="font-semibold flex items-center justify-center min-h-[100px] border border-gray-300 rounded">
                    No users assigned to teams...
                  </p>
                )}
              </div>
            </SortableContext>
          </DndContext>

          <p className="text-sm font-semibold">
            <strong>Speaker Point Scale:</strong>{' '}
            <span className="text-red-600">&lt;20: Unethical</span> •{' '}
            <span className="text-red-500">20–23: Below Average</span> •{' '}
            <span className="text-gray-500">24–26: Average</span> •{' '}
            <span className="text-green-600">27–28: Above Average</span> •{' '}
            <span className="text-green-700 font-semibold">29–30: Outstanding</span>
          </p>

          <div className="mt-6 space-y-4">
            <div className="font-semibold">Decision</div>
            <div className="flex w-full justify-between items-center">
              <div className="flex  gap-4">
                <label>
                  <input
                    type="radio"
                    name="decision"
                    value={SpeakingRole.Affirmative}
                    checked={decision === SpeakingRole.Affirmative}
                    onChange={() => setDecision(SpeakingRole.Affirmative)}
                  />
                  <span className="ml-2">Affirmative</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="decision"
                    value={SpeakingRole.Negative}
                    checked={decision === SpeakingRole.Negative}
                    onChange={() => setDecision(SpeakingRole.Negative)}
                  />
                  <span className="ml-2">Negative</span>
                </label>
              </div>
              <label className="flex items-center gap-1 ml-6">
                <input
                  type="checkbox"
                  checked={isLowPointWin}
                  onChange={(e) => setIsLowPointWin(e.target.checked)}
                />
                <span className="ml-1">Low-point win?</span>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info
                      size="20px"
                      className="cursor-pointer text-black dark:text-muted-foreground"
                    />
                  </TooltipTrigger>
                  <TooltipContent side="left" className="w-72 text-sm text-left">
                    <ul className="space-y-2 list-disc list-inside">
                      <li>
                        A <strong>low-point win</strong> occurs when the judge determines that one
                        team <em>wins the debate</em> but gave{' '}
                        <em>lower-quality speaking performances</em> than the other.
                      </li>
                      <li>
                        This might happen if a team was more persuasive on arguments but less
                        effective in style, clarity, or delivery.
                      </li>
                      <li>
                        Most common in formats like <strong>LD</strong> or <strong>Policy</strong>{' '}
                        where argument quality and speaker skill are both judged independently.
                      </li>
                    </ul>
                  </TooltipContent>
                </Tooltip>
              </label>
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <label className="block font-semibold mb-1">Reasons for Decision</label>
              <Textarea
                className="w-full"
                rows={5}
                value={reasons}
                onChange={(e) => setReasons(e.target.value)}
                placeholder="Cite key arguments that determined the winner..."
              />
            </div>
            <div>
              <label className="block font-semibold mb-1">Comments to Debaters</label>
              <Textarea
                className="w-full"
                rows={4}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Constructive feedback on logic, case, delivery, etc."
              />
            </div>
          </div>

          <div className="flex justify-end mt-6 pb-6">
            <div className="flex items-center gap-4">
              <Button variant="destructive" onClick={setDefaults} disabled={isSubmitting}>
                Reset
              </Button>
              <Button onClick={handleSubmit} disabled={!validateBallot() || isSubmitting}>
                {isSubmitting ? 'Submitting...' : usersBallot ? 'Update' : 'Submit'} Ballot
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
